<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

import SidebarUserMenu from "@/components/sidebar/SidebarUserMenu.vue";

defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["menu-open-change"]);

const store = useStore();

const user = computed(() => store.getters.getUser);
const team = computed(() => store.getters.getTeam);
const isStaffOnCloud = computed(() => store.getters.isStaffOnCloud);
const isTeamAdmin = computed(() => store.getters.isTeamAdmin);

const displayName = computed(() => user.value?.user_name || user.value?.email || "");

const teamName = computed(() => team.value?.name);
const userEmail = computed(() => user.value?.email || "");

const avatarIcon = computed(() => {
  if (isStaffOnCloud.value) return "bi-shield-check";
  if (isTeamAdmin.value) return "bi-shield";

  return "bi-person-circle";
});
</script>

<template>
  <div class="sidebar-user-footer">
    <SidebarUserMenu :collapsed="collapsed" @menu-open-change="emit('menu-open-change', $event)">
      <template #default>
        <div v-if="!collapsed" class="sidebar-user-footer-expanded">
          <div
            class="sidebar-user-footer-avatar"
            :class="{ 'staff-avatar': isStaffOnCloud, 'admin-avatar': !isStaffOnCloud && isTeamAdmin }"
          >
            <i :class="avatarIcon" />
          </div>

          <div class="sidebar-user-footer-info">
            <div v-if="teamName" class="sidebar-user-footer-team truncate">{{ teamName }}</div>
            <div class="sidebar-user-footer-name truncate">{{ displayName }}</div>
            <div v-if="!user?.user_name" class="sidebar-user-footer-email truncate">{{ userEmail }}</div>
          </div>

          <div class="sidebar-user-footer-chevron">
            <i class="bi-chevron-expand" />
          </div>
        </div>

        <div v-else class="sidebar-user-footer-collapsed">
          <div
            class="sidebar-user-footer-avatar"
            :class="{ 'staff-avatar': isStaffOnCloud, 'admin-avatar': !isStaffOnCloud && isTeamAdmin }"
          >
            <i :class="avatarIcon" />
          </div>
        </div>
      </template>
    </SidebarUserMenu>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-user-footer {
  @apply shrink-0 p-3;
}

.sidebar-user-footer-expanded {
  @apply flex items-center gap-2.5 p-2 rounded-lg transition-colors duration-200;

  &:hover {
    background-color: color-mix(in oklch, oklch(var(--p)) 5%, transparent);
  }
}

.sidebar-user-footer-collapsed {
  @apply flex justify-center items-center p-2 rounded-lg transition-colors duration-200;

  &:hover {
    background-color: color-mix(in oklch, oklch(var(--p)) 5%, transparent);
  }
}

.sidebar-user-footer-avatar {
  @apply flex items-center justify-center shrink-0 text-2xl;
  color: oklch(var(--bc));

  &.staff-avatar {
    @apply text-staff;
  }

  &.admin-avatar {
    @apply text-primary;
  }
}

.sidebar-user-footer-info {
  @apply flex-1 min-w-0 leading-tight;

  .sidebar-user-footer-team {
    @apply text-xs font-semibold;
    color: oklch(var(--p));
  }

  .sidebar-user-footer-name {
    @apply text-sm font-semibold;
    color: oklch(var(--bc));
  }

  .sidebar-user-footer-email {
    @apply text-xs;
    color: oklch(var(--bc) / 0.5);
  }
}

.sidebar-user-footer-chevron {
  @apply shrink-0 text-sm;
  color: oklch(var(--bc) / 0.5);
}
</style>
